/**
 * useRumData.ts — Hook for nself-rum plugin (port 3837)
 *
 * Purpose:    Fetch Core Web Vitals, session replays, and JS snippet for the RUM dashboard.
 * Inputs:     None (reads from plugin backend port 3837)
 * Outputs:    { state, data, error, refresh }
 * Constraints: Client-side only; uses pluginFetch; AsyncState for UI
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useCallback, useEffect, useState } from 'react'
import { pluginFetch } from '../lib/plugin-client'
import type { SentryError } from '../lib/sentry-error'
import type { AsyncState } from '../components/AsyncScreen'

const PORT = 3837

export type VitalRating = 'good' | 'needs-improvement' | 'poor'

export interface WebVital {
  name: 'LCP' | 'FID' | 'CLS' | 'TTFB'
  value: number
  unit: 'ms' | 'score'
  rating: VitalRating
  p75: number
}

export interface SessionReplay {
  id: string
  userId?: string
  pageUrl: string
  durationMs: number
  errorCount: number
  recordedAt: string
  replayUrl: string
}

export interface RumData {
  webVitals: WebVital[]
  sessions: SessionReplay[]
  /** Ready-to-copy JS snippet for embedding the RUM SDK */
  jsSnippet: string
  /** Total sessions collected */
  totalSessions: number
}

export function useRumData() {
  const [state, setState] = useState<AsyncState>('loading')
  const [data, setData] = useState<RumData | null>(null)
  const [error, setError] = useState<SentryError | undefined>()

  const load = useCallback(async () => {
    setState('loading')
    setError(undefined)
    if (!navigator.onLine) { setState('offline'); return }
    const result = await pluginFetch<RumData>(PORT, '/api/rum/summary')
    if (result.ok) {
      setData(result.value)
      setState(result.value.totalSessions === 0 ? 'empty' : 'success')
    } else {
      const e = result.error
      if (e.type === 'bundle_required') setState('permission-denied')
      else if (e.type === 'rate_limit') setState('rate-limited')
      else if (e.type === 'plugin_offline') setState('offline')
      else setState('error')
      setError(e)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  return { state, data, error, refresh: load }
}
